import { Injectable, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { LanguageService } from './language.service';
import { environment } from '../../../environments/environment';

export interface SeoConfig {
    title: string;
    description: string;
    keywords?: string;
    image?: string; // Ruta del sitio (`/insights/img/x.webp`) o URL absoluta. Se absolutiza sola
    imageAlt?: string; // Texto alternativo de la imagen social (por defecto, el título).
    url?: string;
    type?: 'website' | 'article' | 'profile';
    noIndex?: boolean;
    author?: string;
}

@Injectable({
    providedIn: 'root'
})
export class SeoService {
    private titleService = inject(Title);
    private metaService = inject(Meta);
    private languageService = inject(LanguageService);
    private document = inject(DOCUMENT);

    private readonly fallbackOrigin = 'https://iki.health';
    private readonly siteName = 'IKI Health';

    /**
     * Hosts sin significado público. `ng-localhost` es el que usa el prerender de
     * Angular al generar las rutas estáticas en build: ahí no hay petición real,
     * así que esas páginas deben quedarse con el origen canónico.
     */
    private readonly syntheticHosts = ['ng-localhost'];

    /**
     * Origen real desde el que se sirve la página: en el navegador `location.origin`
     * y en SSR el que `platform-server` deriva de la URL de la petición.
     */
    get origin(): string {
        const location = this.document.location;
        const origin = location?.origin;
        if (!origin || !origin.startsWith('http')) return this.fallbackOrigin;
        if (this.syntheticHosts.includes(location.hostname)) return this.fallbackOrigin;
        return origin;
    }

    // Absolutiza una ruta del sitio (`/insights/x.webp`) contra el origen actual
    absoluteUrl(pathOrUrl: string): string {
        if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
        return `${this.origin}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
    }

    // MIME de la imagen social a partir de su extensión (og:image:type)
    private imageMimeType(url: string): string | null {
        const ext = url.split(/[?#]/)[0].split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'webp': return 'image/webp';
            case 'png': return 'image/png';
            case 'jpg':
            case 'jpeg': return 'image/jpeg';
            case 'gif': return 'image/gif';
            case 'svg': return 'image/svg+xml';
            default: return null;
        }
    }

    /**
     * Obtiene la imagen OG por defecto según el idioma
     */
    private getDefaultOgImage(): string {
        const currentLang = this.languageService.getCurrentLanguage();
        return currentLang === 'es'
            ? `${this.origin}/og-default-es.png`
            : `${this.origin}/og-default-en.png`;
    }

    /**
     * Actualiza los meta tags de SEO para la página actual
     * @param config Configuración de SEO (título, descripción, keywords, etc.)
     */
    updateMetaTags(config: SeoConfig): void {
        const currentLang = this.languageService.getCurrentLanguage();
        const seoConfig: Required<SeoConfig> = {
            title: config.title,
            description: config.description,
            keywords: config.keywords || '',
            image: this.absoluteUrl(config.image || this.getDefaultOgImage()),
            imageAlt: config.imageAlt || config.title,
            url: this.absoluteUrl(config.url || this.origin),
            type: config.type || 'website',
            noIndex: config.noIndex || !environment.production, // Los entornos que no son producción nunca se indexan
            author: config.author || this.siteName
        };

        // Title
        this.titleService.setTitle(seoConfig.title);

        // Standard Meta Tags
        this.metaService.updateTag({ name: 'description', content: seoConfig.description });

        if (seoConfig.keywords) {
            this.metaService.updateTag({ name: 'keywords', content: seoConfig.keywords });
        }

        this.metaService.updateTag({ name: 'author', content: seoConfig.author });

        // Robots
        const robotsContent = seoConfig.noIndex ? 'noindex, nofollow' : 'index, follow';
        this.metaService.updateTag({ name: 'robots', content: robotsContent });

        // Language
        this.metaService.updateTag({ httpEquiv: 'content-language', content: currentLang });
        this.metaService.updateTag({ name: 'language', content: currentLang });

        // Open Graph (Facebook, LinkedIn)
        this.metaService.updateTag({ property: 'og:title', content: seoConfig.title });
        this.metaService.updateTag({ property: 'og:description', content: seoConfig.description });
        this.metaService.updateTag({ property: 'og:type', content: seoConfig.type });
        this.metaService.updateTag({ property: 'og:url', content: seoConfig.url });
        this.metaService.updateTag({ property: 'og:image', content: seoConfig.image });
        this.metaService.updateTag({ property: 'og:image:alt', content: seoConfig.imageAlt });
        this.metaService.updateTag({ property: 'og:site_name', content: this.siteName });
        this.metaService.updateTag({ property: 'og:locale', content: currentLang === 'es' ? 'es_ES' : 'en_US' });

        // og:image:secure_url + og:image:type: Facebook/LinkedIn los usan para decidir si pueden renderizar la imagen sin descargarla primero.
        if (seoConfig.image.startsWith('https://')) {
            this.metaService.updateTag({ property: 'og:image:secure_url', content: seoConfig.image });
        }
        const mime = this.imageMimeType(seoConfig.image);
        if (mime) {
            this.metaService.updateTag({ property: 'og:image:type', content: mime });
        } else {
            this.metaService.removeTag('property="og:image:type"');
        }

        // Twitter Cards
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ name: 'twitter:title', content: seoConfig.title });
        this.metaService.updateTag({ name: 'twitter:description', content: seoConfig.description });
        this.metaService.updateTag({ name: 'twitter:image', content: seoConfig.image });
        this.metaService.updateTag({ name: 'twitter:image:alt', content: seoConfig.imageAlt });
        this.metaService.updateTag({ name: 'twitter:site', content: '@ikihealth' });

        // Update canonical URL
        this.updateCanonicalUrl(seoConfig.url);
    }

    /**
     * Actualiza o crea la URL canónica
     * @param url URL canónica de la página
     */
    updateCanonicalUrl(url: string): void {
        const head = this.document.head;
        let linkCanonical: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');

        if (!linkCanonical) {
            linkCanonical = this.document.createElement('link');
            linkCanonical.setAttribute('rel', 'canonical');
            head.appendChild(linkCanonical);
        }

        linkCanonical.setAttribute('href', url);
    }

    /**
     * Añade etiquetas hreflang para URLs multiidioma
     * @param alternateUrls Mapa de idiomas y URLs alternativas
     */
    addHreflangTags(alternateUrls: { [lang: string]: string }): void {
        const head = this.document.head;

        // Eliminar hreflang anteriores
        const existingHreflang = this.document.querySelectorAll('link[rel="alternate"]');
        existingHreflang.forEach(link => link.remove());

        // Añadir nuevas hreflang
        Object.entries(alternateUrls).forEach(([lang, url]) => {
            const link = this.document.createElement('link');
            link.setAttribute('rel', 'alternate');
            link.setAttribute('hreflang', lang);
            link.setAttribute('href', url);
            head.appendChild(link);
        });

        // Añadir x-default
        const defaultUrl = alternateUrls['es'] || alternateUrls['en'] || Object.values(alternateUrls)[0];
        if (defaultUrl) {
            const xDefaultLink = this.document.createElement('link');
            xDefaultLink.setAttribute('rel', 'alternate');
            xDefaultLink.setAttribute('hreflang', 'x-default');
            xDefaultLink.setAttribute('href', defaultUrl);
            head.appendChild(xDefaultLink);
        }
    }

    /**
     * Añade Structured Data (JSON-LD) al head
     * @param schema Objeto de schema.org
     */
    addStructuredData(schema: any): void {
        const head = this.document.head;
        let script: HTMLScriptElement | null = this.document.querySelector('script[type="application/ld+json"]');

        // Si ya existe un script JSON-LD, eliminarlo
        if (script) {
            script.remove();
        }

        // Crear nuevo script
        script = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(schema);
        head.appendChild(script);
    }

    /**
     * Elimina el Structured Data actual
     */
    removeStructuredData(): void {
        const script = this.document.querySelector('script[type="application/ld+json"]');
        if (script) {
            script.remove();
        }
    }

    /**
     * Crea un schema Organization para la página principal
     */
    getOrganizationSchema(): any {
        return {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            'name': this.siteName,
            'url': this.origin,
            'logo': {
                '@type': 'ImageObject',
                'url': `${this.origin}/logo-iki.svg`,
                'width': '250',
                'height': '60'
            },
            'description': 'Clinical operating system that unites the physical and emotional, the subjective and measurable, day-to-day with long-term vision.',
            'foundingDate': '2022',
            'founder': {
                '@type': 'Person',
                'name': 'Patricia Puiggròs',
                'jobTitle': 'CEO & Co-founder'
            },
            'sameAs': [
                'https://www.linkedin.com/company/iki-health',
                'https://twitter.com/ikihealth',
                'https://www.instagram.com/iki.health'
            ],
            'contactPoint': {
                '@type': 'ContactPoint',
                'contactType': 'Customer Service',
                'email': 'info@iki.health',
                'areaServed': ['ES', 'US', 'GB'],
                'availableLanguage': ['es', 'en']
            }
        };
    }

    /**
     * Crea un schema WebSite básico (sin SearchAction si no existe búsqueda)
     */
    getWebSiteSchema(): any {
        return {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            'name': this.siteName,
            'url': this.origin,
            'description': 'Digital health platform for lifestyle medicine and patient monitoring',
            'publisher': {
                '@type': 'Organization',
                'name': this.siteName,
                'logo': {
                    '@type': 'ImageObject',
                    'url': `${this.origin}/logo-iki.svg`
                }
            },
            'inLanguage': ['es', 'en']
        };
    }

    /**
     * Crea un schema Person para perfil de profesional
     * @param professional Datos del profesional
     */
    getPersonSchema(professional: {
        name: string;
        jobTitle?: string;
        description?: string;
        image?: string;
        url?: string;
    }): any {
        return {
            '@context': 'https://schema.org',
            '@type': 'Person',
            'name': professional.name,
            'jobTitle': professional.jobTitle || 'Health Coach',
            'description': professional.description || '',
            'image': professional.image || this.getDefaultOgImage(),
            'url': professional.url || this.origin,
            'memberOf': {
                '@type': 'Organization',
                'name': this.siteName
            }
        };
    }

    /**
     * Restablece los meta tags a los valores por defecto
     */
    setDefaultMetaTags(): void {
        const currentLang = this.languageService.getCurrentLanguage();

        const defaultConfig: Record<string, SeoConfig> = {
            es: {
                title: 'IKI Health - Plataforma de Salud Integral',
                description: 'Sistema operativo clínico que une lo físico y lo emocional, lo subjetivo y lo medible, el día a día con la visión a largo plazo.',
                keywords: 'salud integral, bienestar, telemedicina, profesionales de la salud, medicina de estilo de vida',
                url: `${this.origin}/es`,
                type: 'website'
            },
            en: {
                title: 'IKI Health - Comprehensive Health Platform',
                description: 'Clinical operating system that unites the physical and emotional, the subjective and measurable, day-to-day with long-term vision.',
                keywords: 'comprehensive health, wellness, telemedicine, health professionals, lifestyle medicine',
                url: `${this.origin}/en`,
                type: 'website'
            }
        };

        this.updateMetaTags(defaultConfig[currentLang] || defaultConfig['es']);

        // Añadir hreflang tags
        this.addHreflangTags({
            'es': `${this.origin}/es`,
            'en': `${this.origin}/en`
        });

        // Añadir Organization + WebSite schema
        const combinedSchema = [
            this.getOrganizationSchema(),
            this.getWebSiteSchema()
        ];
        this.addStructuredData(combinedSchema);
    }
}
